import { useAtomValue, useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { unwrappedEventsDataDefaultColumnsAtom } from '@/atoms/dataLocker/events/table';
import { eventLinkAtom, playerLinkAtom, playerPreviewLink, videoPreviewPageLink } from '@/atoms/navigation';
import { eventDataFilterParamsAtom, eventsDataCountAtom } from '@/atoms/queries/eventData';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { EventDataControls } from '@/components/controls/EventsDataControls';
import { DataLockerEventsTable } from '@/components/dataLocker/DataLockerEventsTable';
import { PageTitle } from '@/components/head/PageTitle';
import { DataLockerLayoutWithFilters } from '@/components/layout/dataLocker/DataLockerLayoutWithFilters';
import { VisSectionContainer } from '@/components/layout/VisSectionContainer';
import { VisDisplaySection } from '@/components/section/VisDisplaySection';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { EventCountInfoBar } from '@/components/visualisations/EventCountInfoBar';
import { PitchVisualisation } from '@/components/visualisations/PitchVisualisation/PitchVisualisation';
import { EVENTS_FILTER_ATOMS, EVENTS_TABLE_COLUMN_ENTITIES } from '@/consts/dataLocker/events';
import { useSetDataLockerDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetDataLockerDebouncedFilterParams';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useAutoResetPagination } from '@/hooks/useAutoResetPagination';
import { useTableColumns } from '@/hooks/useTableColumns';
import { eventsUrl } from '@/query/url';
import { TableId } from '@/types/table';
import { VisDisplayMode } from '@/types/visualisation';
import { dataLockerVisualisationComponents } from '../visComponents';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const filterParams = useAtomValue(eventDataFilterParamsAtom);
  useCsvDownload(pageTitle, eventsUrl(filterParams));
  return null;
};

const SetLinkBehaviour = () => {
  const setEventLink = useSetAtom(eventLinkAtom);
  const setPlayerLink = useSetAtom(playerLinkAtom);

  // useLayoutEffect (not useEffect) so the scoped links are set before paint — otherwise a classic-mode
  // user can see/click the classic dashboard anchor on the first render before the preview link applies.
  useLayoutEffect(() => {
    setEventLink(videoPreviewPageLink);
    setPlayerLink(playerPreviewLink);
  }, []);

  return null;
};

const VisDisplaySectionWithControls = () => {
  useTableColumns(EVENTS_TABLE_COLUMN_ENTITIES);

  return (
    <>
      <EventDataControls defaultDisplayMode={VisDisplayMode.TABLE} />
      <VisDisplaySection
        EventsTableComponent={DataLockerEventsTable}
        visualisationComponent={
          <PitchVisualisation
            visualisations={dataLockerVisualisationComponents}
            footerComponent={<EventCountInfoBar eventCountAtom={eventsDataCountAtom} />}
          />
        }
      />
    </>
  );
};

const VisDisplaySectionWithControlsWrapper = ({ pageTitle }: { pageTitle: string }) => (
  <VisSectionContainer>
    <ScopedTableWithDefaultColumns
      defaultColumnsAtom={unwrappedEventsDataDefaultColumnsAtom}
      tableId={TableId.EVENT_DATA}
    >
      <CsvDownload pageTitle={pageTitle} />
      <SetLinkBehaviour />
      <VisDisplaySectionWithControls />
    </ScopedTableWithDefaultColumns>
  </VisSectionContainer>
);

const AutoResetPagination = () => {
  const filterParams = useAtomValue(eventDataFilterParamsAtom);
  useAutoResetPagination(filterParams);
  return null;
};

export const DebouncedEventsFilterParams = () => {
  useSetDataLockerDebouncedFilterParams(EVENTS_FILTER_ATOMS);
  return null;
};

export const EventDataLockerPage = () => {
  const { t } = useTranslation('entity');
  const eventDataLockerPageTitle = t('event_other');

  return (
    <>
      <AutoResetPagination />
      <DebouncedEventsFilterParams />
      <ScopeProvider atoms={[tableColumnEntitiesAtom, eventLinkAtom, playerLinkAtom]}>
        <PageTitle>{eventDataLockerPageTitle}</PageTitle>
        <DataLockerLayoutWithFilters>
          <VisDisplaySectionWithControlsWrapper pageTitle={eventDataLockerPageTitle} />
        </DataLockerLayoutWithFilters>
      </ScopeProvider>
    </>
  );
};
